import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Box,
    InputBase,
    Avatar,
    Menu,
    MenuItem,
    Typography,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {

    ConfirmationNumber as ConfirmationNumberIcon,
    Person as PersonIcon,
    ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { getAuthToken, handleLogout, getAudienceProfile } from '@virtual-cinema/client-lib';

const Logo = ({ isMobile }) => (
    <Typography
        component="span"
        sx={{
            fontFamily: "'Edu AU VIC WA NT Hand', cursive",
            fontSize: isMobile ? '2rem' : '2.5rem',
            color: '#ffffff',
            textDecoration: 'none',
            position: 'relative',
            display: 'inline-block',
            textShadow: '0 0 10px rgba(255,255,255,0.3)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                transform: 'scale(1.05)',
                textShadow: '0 0 15px rgba(255,255,255,0.5)',
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '2px',
                background: 'rgba(255,255,255,0.3)',
                transform: 'scaleX(0)',
                transformOrigin: 'right',
                transition: 'transform 0.3s ease-in-out',
            },
            '&:hover::after': {
                transform: 'scaleX(1)',
                transformOrigin: 'left',
            },
        }}
    >
        kalakaar
    </Typography>
);

const AudienceAppBar = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const accessToken = getAuthToken();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (accessToken) {
                setLoading(true);
                try {
                    const response = await getAudienceProfile(accessToken);
                    if (response?.data) {
                        setUserProfile(response.data);
                    }
                } catch (err) {
                    console.error('Error fetching Audience profile:', err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchUserProfile();
    }, [accessToken]);

    const getDisplayName = (profile) => {
        if (!profile?.info) return '';
        const { firstname, lastname } = profile.info;
        if (firstname && lastname) return `${firstname} ${lastname}`;
        if (firstname) return firstname;
        if (lastname) return lastname;
        return '';
    };

    const getAvatarLetter = (profile) => {
        if (!profile?.info?.firstname) return 'K';
        return profile.info.firstname[0].toUpperCase();
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogin = () => {
        navigate("/login");
    };

    return (
        <AppBar
            position="static"
            sx={{
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(8px)',
                boxShadow: 'none',
            }}
        >
            <Toolbar sx={{
                justifyContent: 'space-between',
                padding: theme.spacing(2, 3),
                [theme.breakpoints.down('sm')]: {
                    padding: theme.spacing(1.5, 2),
                },
            }}>
                <Box component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
                    <Logo isMobile={isMobile} />
                </Box>

                {/* Search box will be implemented later */}

                {accessToken ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={handleMenu}
                            size="small"
                            sx={{ ml: 0.5 }}
                        >
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {getAvatarLetter(userProfile)}
                            </Avatar>
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                sx: {
                                    width: 280,
                                    mt: 1,
                                    '& .MuiMenuItem-root': {
                                        minHeight: 'unset',
                                        py: 1.5,
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                                        }
                                    }
                                }
                            }}
                        >
                            {userProfile && (
                                <>
                                    <MenuItem
                                        disabled
                                        sx={{
                                            padding: '12px 16px',
                                            '&.Mui-disabled': {
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0
                                        }}>
                                            <Box sx={{
                                                fontWeight: 500,
                                                color: 'text.primary',
                                                mb: 0.5
                                            }}>
                                                {getDisplayName(userProfile)}
                                            </Box>
                                            <Box sx={{
                                                fontSize: '0.875rem',
                                                color: 'text.secondary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {userProfile.email}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                    <Divider />
                                </>
                            )}

                            <MenuItem onClick={() => {
                                handleClose();
                                navigate('/myTickets');
                            }}>
                                <ListItemIcon>
                                    <ConfirmationNumberIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>My Tickets</ListItemText>
                            </MenuItem>

                            <MenuItem onClick={() => {
                                handleClose();
                                navigate('/profile');
                            }}>
                                <ListItemIcon>
                                    <PersonIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Profile</ListItemText>
                            </MenuItem>

                            <MenuItem onClick={() => {
                                handleClose();
                                handleLogout();
                            }}>
                                <ListItemIcon>
                                    <ExitToAppIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Logout</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                ) : (
                    <Box
                        component="button"
                        onClick={handleLogin}
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: '600',
                            fontSize: '14px',
                            padding: '10px 25px',
                            color: '#fff',
                            borderRadius: '0',
                            border: '1px solid rgba(255,255,255,0.2)',
                            backgroundColor: 'rgba(61, 63, 73, 0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(91, 93, 104, 0.4)',
                                borderColor: 'rgba(255,255,255,0.4)',
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        Login
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default AudienceAppBar;